import { CurrencyPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { Router } from '@angular/router';
import { GetAccount, UpdateCurrentAccountId } from '@app/finxone-web-frontend/app/lib/actions/account.action';
import {
  UpdateActiveSavingsAccountId,
  UpdateInternalTransferFromAccountId,
} from '@app/finxone-web-frontend/app/lib/actions/savings-account.action';
import { Country } from '@app/finxone-web-frontend/app/lib/dtos/countries.dto';
import { Currency } from '@app/finxone-web-frontend/app/lib/dtos/currencies.dto';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { AccountState, AccountStateModel } from '@app/finxone-web-frontend/app/lib/state/account.state';
import { ApiActions, ApiLoadingState } from '@app/finxone-web-frontend/app/lib/state/api-loading.state';
import { ProjectSettingsState } from '@app/finxone-web-frontend/app/lib/state/project-settings.state';
import {
  SavingsAccountState,
  SavingsAccountStateModel,
} from '@app/finxone-web-frontend/app/lib/state/savings-account.state';
import { scrollLoading } from '@app/finxone-web-frontend/app/lib/utils/scrolling-utils/scrolling.utils';
import { buildHandleBarModel } from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { resolveAccountIdentifierFields } from '@app/finxone-web-frontend/app/lib/utils/account-identifier.utils';
import { addDashesToSortCode } from '@app/finxone-web-frontend/app/lib/utils/template/string.utils';
import {
  getCountryNameByCountryCode,
  getCurrencyDataByKey,
} from '@app/finxone-web-frontend/app/lib/utils/utils';
import { formatNameAsUrl } from '@app/finxone-web-frontend/app/lib/utils/zone-url.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { IconCacheService } from '@finxone-platform/shared/services';
import {
  APP_ZONES,
  AccountInfoType,
  AccountStatusType,
  AccountTypes,
  AppBuilderPreviewState,
  BaseWidgetProperties,
  NavigationOptionsType,
  PaymentProvider,
  ProviderClass,
  SKELETON_LAYOUT_CONFIG_PATH,
  SavingsAccountInfoType,
  ViewType,
  WidgetNames,
  ZoneActionOptions,
  baseCssConfigStyle,
  handleExternalNavigation,
  isMultiCurrencyAccountInfoType,
  isSavingsAccountInfoType,
  resolveSkeletonLoadingTemplate,
  resolveSkeletonRenderOptions,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { Observable, Subject, map, take, takeUntil } from 'rxjs';
import { PageConfigurationProvider } from '../../../../zones/page-configuration.provider';
import { ZonePageUrlProvider } from '../../../../zones/zone-url-provider';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-accounts-listing',
  templateUrl: './accounts-listing.component.html',
  styleUrl: './accounts-listing.component.scss',
})
export class AccountsListingComponent implements OnInit, AfterViewInit {
  @AutoDestroy private destroy$: Subject<void> = new Subject<void>();
  @Select(AccountState.getCurrentAccountId)
  currentAccountId$!: Observable<string>;
  @Output() selectedAccountEmit = new EventEmitter<string>();
  @Output() scrolled = new EventEmitter<void>();
  @Output() tablePaginationAndSearch = new EventEmitter<{
    accountContextStatusList: AccountStatusType[];
    searchTerm: { name: string } | undefined;
    viewMode: ViewType;
    pageSize: number;
    page?: number;
  }>();
  public fetchingTemplate = '';
  @Input() set widgetConfigData(widgetConfig: BaseWidgetProperties) {
    this.widgetConfig = widgetConfig;
    this.setMissingDataIfNeeded();
  }
  widgetConfig: BaseWidgetProperties;
  @Input() contentStyle: baseCssConfigStyle;
  @Input() searchIconStyle: baseCssConfigStyle;
  @Input() textContentStyle: baseCssConfigStyle;
  @Input() context: AccountTypes | 'account' = 'account';
  @Input() accountsInfo: AccountStateModel | SavingsAccountStateModel;
  @Input() currencies: Currency[];
  @Input() countries: Country[];
  @Input('contextSwitch')
  set ContextSwitch(contextSwitch: boolean | undefined) {
    this.contextSwitch = contextSwitch ?? true;
  }
  @Input() navigateToUrl: string;
  @Input() public accountsData: (AccountInfoType | SavingsAccountInfoType)[];
  @Input() public accountContextStatusList: AccountStatusType[];
  @Input() public widgetContext: string;
  @Input() totalListCount: number;
  public containerHeight = '100%';
  private contextSwitch: boolean;
  public currentStyle: baseCssConfigStyle;
  public content = '';
  public noRecordsTemplate = '';
  public searchText = '';
  public baseHandleBarModel: any;
  public activeAccountIndex = 0;
  public templateFunction: HandlebarsTemplateDelegate<any>;
  public noRecordsTemplateFunction: HandlebarsTemplateDelegate<any>;
  public fetchingTemplateFunction: HandlebarsTemplateDelegate<any>;
  currentZoneFromRoute: Record<string, string | number>;
  @Select(AccountState.getAccounts)
  private accountState$!: Observable<AccountStateModel>;
  @Select(SavingsAccountState.getAccounts)
  public savingsAccounts$!: Observable<SavingsAccountStateModel>;
  public menuPopupCss = {};
  public viewType = ViewType.LIST;
  public layoutType = ViewType;
  public enableClickableRow = true;
  public actionOptions: ZoneActionOptions[] = [];
  public pageNumber = 1;
  public carouselItemStyles: baseCssConfigStyle & { widthUnit: string } = {
    width: '100',
    height: '100%',
    widthUnit: '%',
  };

  isApiInProgress = false;
  public appBuilderShowFetchingContent = false;
  public appBuilderShowNoRecordsContent = false;

  public WidgetNames = WidgetNames;
  // a linked list takes each row's box from the theme's List styling
  public get isGlobalStylingEnabled(): boolean {
    return !!this.dataIn?.attri?.widgetProperties?.['globalStyling'];
  }

  constructor(
    private store: Store,
    private currencyPipe: CurrencyPipe,
    private _router: Router,
    private countriesCurrenciesService: CountriesCurrenciesService,
    private zonePageUrlProvider: ZonePageUrlProvider,
    private iconCacheService: IconCacheService,
    private el: ElementRef,
    public pageConfig: PageConfigurationProvider,
    public dataIn: ProviderClass<any>,
    public configService: ConfigService,
    private router: Router,
    private ref: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.subscribeToApiLoadingAction();
    this.updateStyleProperties();
    this.initHandleBars();
    this.onLoadWidgetConfig();
    this.setAppBuilderPreviewState();
    if (this.context === 'savings-account') {
      this.savingsAccounts$
        .pipe(
          map((response) => {
            this.accountsInfo = response;
            this.accountsData = response.accounts;
            this.totalListCount = response.totalItems;
            this.activeAccountIndex = this.accountsData.findIndex(
              (v) => v.accountId === (this.accountsInfo as SavingsAccountStateModel).activeAccountId,
            );
          }),
        )
        .subscribe();
    } else {
      this.accountState$
        .pipe(
          map((response) => {
            this.accountsInfo = response;
            this.accountsData = response.accounts;
            this.totalListCount = response.totalItems;
            this.activeAccountIndex = this.accountsData.findIndex(
              (v) => v.accountId === (this.accountsInfo as AccountStateModel).currentAccountId,
            );
          }),
        )
        .subscribe();
    }
  }

  private subscribeToApiLoadingAction() {
    const accountsLoadingActionByContext = {
      account: ApiActions.getAccounts,
      [AccountTypes.CURRENT_ACCOUNT]: ApiActions.getAccounts,
      [AccountTypes.SAVINGS_ACCOUNT]: ApiActions.getSavingsAccounts,
    };

    const action =
      accountsLoadingActionByContext[this.context as keyof typeof accountsLoadingActionByContext];
    if (!action) return;
    this.store
      .select(ApiLoadingState.getApiState(action))
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.isApiInProgress = result.isLoading;
      });
  }

  ngAfterViewInit() {
    this.containerHeight = scrollLoading('account-listing', this.el);
    this.ref.detectChanges();
  }

  private onLoadWidgetConfig() {
    this.viewType = this.dataIn.attri?.widgetProperties?.['viewType'] ?? ViewType.LIST;
    this.actionOptions = this.dataIn?.attri?.widgetProperties?.['menuOptions'];
    this.menuPopupCss = {
      'margin-top': '5px',
      'border-radius': '6px',
    };
  }

  updateStyleProperties() {
    this.contentStyle = {
      ...this.contentStyle,
      display: 'block',
    };
    this.carouselItemStyles = {
      height:
        this.dataIn?.attri?.['carouselItemHeight'] + this.dataIn?.attri?.['carouselItemHeightUnit'] || 'auto',
      width: this.dataIn?.attri?.['carouselItemWidth'] ?? 'auto',
      widthUnit: this.dataIn?.attri?.['carouselItemWidthUnit'] ?? '%',
    };
  }
  private initHandleBars() {
    this.content = this.getContentTemplate();
    this.noRecordsTemplate = this.getNoRecordsTemplate();
    this.fetchingTemplate = this.getFetchingTemplate();
    this.templateFunction = Handlebars.compile(this.content);
    this.noRecordsTemplateFunction = Handlebars.compile(this.noRecordsTemplate);
    this.fetchingTemplateFunction = Handlebars.compile(this.fetchingTemplate);
    buildHandleBarModel(
      this.store,
      '',
      this.countriesCurrenciesService,
      this.iconCacheService,
      undefined,
      undefined,
      undefined,
      undefined,
      true, // to avoid auto refresh of state data (accounts listing etc)
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        this.baseHandleBarModel = model;
      });
  }

  public getContentTemplate(): string {
    const templateToUse =
      this.widgetConfig?.['orientation'] === 'vertical'
        ? this.widgetConfig?.textContent
        : this.widgetConfig?.['horizontalLayoutTemplate'];
    return templateToUse || '';
  }

  private getNoRecordsTemplate(): string {
    const templateToUse =
      this.widgetConfig?.['orientation'] === 'vertical'
        ? this.widgetConfig?.['verticalNoRecordsTemplate']
        : this.widgetConfig?.['horizontalNoRecordsTemplate'];
    return templateToUse || '';
  }

  private getFetchingTemplate(): string {
    const templateToUse =
      this.widgetConfig?.['orientation'] === 'vertical'
        ? this.widgetConfig?.['verticalFetchingTemplate']
        : this.widgetConfig?.['horizontalFetchingTemplate'];
    return templateToUse || '';
  }

  searchAccounts(event: any) {
    this.searchText = '';
    if (event.target.value.length) {
      if (!this.accountsData.length) this.accountsData = this.accountsInfo.accounts;

      this.accountsData = this.accountsData.filter((v) => {
        if (isSavingsAccountInfoType(v)) {
          return v.currency.toLowerCase().includes(event.target.value.toLowerCase());
        } else if (isMultiCurrencyAccountInfoType(v)) {
          return v.currency.some((c) => c.currency.toLowerCase().includes(event.target.value.toLowerCase()));
        } else {
          return v.currency.toLowerCase().includes(event.target.value.toLowerCase());
        }
      });
      this.searchText = event.target.value;
    } else {
      this.accountsData = this.accountsInfo.accounts;
    }
  }

  public accountsHandleBarModel(accountInfo: AccountInfoType | SavingsAccountInfoType, index: number) {
    if (this.context === 'savings-account') {
      return this.getSavingsAccountModel(accountInfo as SavingsAccountInfoType, index);
    }
    return this.getAccountHandlebarModel(accountInfo as AccountInfoType, index);
  }

  private getSavingsAccountModel(accountInfo: SavingsAccountInfoType, index: number) {
    const identifierFields = resolveAccountIdentifierFields(accountInfo, undefined);
    return {
      index,
      accountInfo: accountInfo,
      lastItem: index === this.accountsData.length - 1,
      accountCurrency: accountInfo.currency,
      accountBalance: accountInfo.balance,
      indicativeBalance: accountInfo.balance,
      availableBalance: accountInfo.availableBalance,
      alias: accountInfo?.accountName ?? '',
      accountNumber: identifierFields.accountNumber,
      sortCode: identifierFields.sortCode ? addDashesToSortCode(identifierFields.sortCode) : undefined,
      bic: identifierFields.bic,
      ledgerNumber: identifierFields.ledgerNumber,
      iban: identifierFields.iban,
      providerExtraInfo: identifierFields.providerExtraInfo,
      achAccountNumber: identifierFields.achAccountNumber,
      achNumber: identifierFields.achNumber,
      abaAccountNumber: identifierFields.abaAccountNumber,
      abaNumber: identifierFields.abaNumber,
      accountProviderExtraInfo: identifierFields.providerExtraInfo,
      defaultMarker: index === 0 ? '(Default)' : '',
      currencyName: getCurrencyDataByKey('currency_name', accountInfo, this.currencies),
      countryName: getCountryNameByCountryCode(accountInfo.country, this.countries),
      accountStatus: `${accountInfo?.status?.charAt(0)?.toUpperCase()}${accountInfo?.status?.slice(1)}`,
    };
  }

  private getAccountHandlebarModel(accountInfo: AccountInfoType, index: number) {
    const identifierFields = resolveAccountIdentifierFields(accountInfo, undefined);
    return {
      index,
      accountInfo: accountInfo,
      lastItem: index === this.accountsData.length - 1,
      accountCurrency: accountInfo.currency,
      accountBalance: accountInfo.balance,
      indicativeBalance: accountInfo.indicativeBalance,
      availableBalance: accountInfo.availableBalance,
      ledgerBalance: accountInfo?.ledgerBalance ?? '',
      ledgerBalanceIncludingPendingExchange: accountInfo?.ledgerBalanceIncludingPendingExchange ?? '',
      alias: accountInfo?.alias ?? '',
      accountNumber: identifierFields.accountNumber,
      sortCode: identifierFields.sortCode ? addDashesToSortCode(identifierFields.sortCode) : undefined,
      bic: identifierFields.bic,
      ledgerNumber: identifierFields.ledgerNumber,
      iban: identifierFields.iban,
      providerExtraInfo: identifierFields.providerExtraInfo,
      achAccountNumber: identifierFields.achAccountNumber,
      achNumber: identifierFields.achNumber,
      abaAccountNumber: identifierFields.abaAccountNumber,
      abaNumber: identifierFields.abaNumber,
      accountProviderExtraInfo: identifierFields.providerExtraInfo,
      pendingExchangeNetTotal: accountInfo?.scheduledExchanges?.netTotal ?? '',
      pendingExchangeCount: accountInfo?.scheduledExchanges?.totalBooked ?? '',
      pendingTransactionNetTotal: accountInfo?.scheduledPayments?.netTotal ?? '',
      pendingTransactionCount: accountInfo?.scheduledPayments?.totalBooked ?? '',
      defaultMarker: index === 0 ? '(Default)' : '',
      currencyName: getCurrencyDataByKey('currency_name', accountInfo, this.currencies),
      countryName: getCountryNameByCountryCode(accountInfo.country, this.countries),
      accountStatus: `${accountInfo?.status?.charAt(0)?.toUpperCase()}${accountInfo?.status?.slice(1)}`,
    };
  }

  public renderNoRecordsTemplate() {
    return this.noRecordsTemplateFunction({
      ...this.baseHandleBarModel,
    });
  }

  public renderFetchingTemplate() {
    return resolveSkeletonLoadingTemplate(
      this.dataIn?.attri?.[SKELETON_LAYOUT_CONFIG_PATH],
      this.fetchingTemplateFunction({
        ...this.baseHandleBarModel,
      }),
      this.viewType,
      resolveSkeletonRenderOptions(this.dataIn?.attri),
    );
  }

  /**
   * Preview states belong to the app builder canvas only, so a project published while one was
   * selected still loads its real loading and empty states for a customer. Both flags are assigned
   * rather than only set, so switching the preview back to Default clears them.
   */
  private setAppBuilderPreviewState(): void {
    if (!this.configService.getActiveConfigType().isWorkflow) {
      return;
    }
    const appBuilderState = this.dataIn?.attri?.widgetProperties?.['appBuilderState'];
    this.appBuilderShowFetchingContent = appBuilderState === AppBuilderPreviewState.FETCHING;
    this.appBuilderShowNoRecordsContent = appBuilderState === AppBuilderPreviewState.NO_RECORDS;
  }

  /**
   * The canvas loads real accounts from the mock services, so a previewed state would render on
   * top of them. These two stand in for an in-flight first page with nothing to show yet, which is
   * what makes the table draw its skeleton rows.
   */
  public get listingApiInProgress(): boolean {
    return this.isApiInProgress || this.appBuilderShowFetchingContent;
  }

  public get listingAccountsData(): (AccountInfoType | SavingsAccountInfoType)[] {
    return this.appBuilderShowFetchingContent || this.appBuilderShowNoRecordsContent
      ? []
      : (this.accountsData ?? []);
  }

  public onAccountClick(accountInfo: AccountInfoType) {
    this.selectedAccountEmit.emit(accountInfo.accountId);
    if (this.context === 'savings-account') {
      this.store.dispatch(new UpdateInternalTransferFromAccountId(accountInfo.accountId));
      if (this.contextSwitch) {
        this.store.dispatch(new UpdateActiveSavingsAccountId(accountInfo.accountId));
      }
    } else {
      if (this.contextSwitch) {
        this.store.dispatch(new UpdateCurrentAccountId(accountInfo.accountId));
      }
    }
    this.handleAccountNavigation();
  }

  private handleAccountNavigation() {
    // Wait for the store to update with the new account ID before navigating (if contextSwitch is enabled)
    if (this.contextSwitch) {
      this.currentAccountId$.pipe(take(1), takeUntil(this.destroy$)).subscribe(() => {
        this.navigateToDestination();
      });
    } else {
      this.navigateToDestination();
    }
  }

  private navigateToDestination() {
    if (this.navigateToUrl) {
      this._router.navigateByUrl(this.navigateToUrl);
    } else if (this.zonePageUrlProvider.zoneUrlInput === formatNameAsUrl(APP_ZONES.PROFILE)) {
      this._router.navigateByUrl('/zones/profile/bank-statements-preset-downloads');
    } else if (this.zonePageUrlProvider.zoneUrlInput === formatNameAsUrl(APP_ZONES.PAYMENT)) {
      this._router.navigateByUrl('/zones/accounts/account-transactions');
    }
  }

  public fetchNewAccounts(accountIndex: number) {
    const isLastIndex = accountIndex === this.accountsData.length - 1;
    if (isLastIndex) {
      this.scrolled.emit();
    }
  }

  @HostListener('scroll', ['$event'])
  onScroll(event: Event) {
    const element = event.target as HTMLElement;
    const scrollPercentage = (element.scrollTop / (element.scrollHeight - element.clientHeight)) * 100;
    if (scrollPercentage > 80) {
      this.scrolled.emit();
    }
    this.activateWindowScroll(event);
  }

  public onCarouselScroll() {
    this.scrolled.emit();
  }

  public activateWindowScroll(event: Event) {
    const element = event.target as HTMLElement;
    if (Math.round(element.scrollHeight - element.scrollTop) === element.clientHeight) {
      window.scrollBy(0, 100);
    }
    if (element.scrollTop === 0) {
      window.scrollBy(0, -100);
    }
  }

  private setMissingDataIfNeeded() {
    if (!this.widgetConfig?.['orientation']) {
      this.widgetConfig['orientation'] = 'vertical';
    }
    if (
      this.widgetConfig.inputFieldSettings &&
      !this.widgetConfig?.inputFieldSettings?.css_config?.['inputWidth']
    ) {
      this.widgetConfig.inputFieldSettings.css_config['inputWidth'] = 100;
    }
    if (
      this.widgetConfig.inputFieldSettings &&
      !this.widgetConfig?.inputFieldSettings?.css_config?.['inputPosition']
    ) {
      this.widgetConfig.inputFieldSettings.css_config['inputPosition'] = 'start';
    }
  }

  public onPageChange(event: { first: number; page: number; pageCount: number; rows: number }) {
    this.pageNumber = event.first / event.rows + 1;
    this.dataIn.attri['pageSize'] = event.rows;
    this.tablePaginationAndSearch.emit({
      accountContextStatusList: this.accountContextStatusList,
      searchTerm: undefined,
      viewMode: ViewType.TABLE,
      pageSize: event.rows,
      page: event.page,
    });
  }

  public handleTableAction(event: any) {
    this.buildAndDispatchAccountAction(
      event.actionConfig?.action,
      event.data?.accountId || '',
      event.actionConfig?.navigationOptionConfig,
    );
  }

  public onListSwipeAction(actionEvent: any, accountData: AccountInfoType) {
    this.buildAndDispatchAccountAction(
      actionEvent?.action,
      accountData?.accountId || '',
      actionEvent?.config?.navigationOptionConfig,
    );
  }

  private buildAndDispatchAccountAction(
    actionKey: string,
    actionId: string,
    navigationConfig: NavigationOptionsType,
  ) {
    if (!actionKey || !actionId) return;

    const data = {
      actionKey,
      actionId,
      actionOption: {
        zoneToNavigate: navigationConfig?.zone,
        urlToNavigate: navigationConfig?.pageUrl,
      } as ZoneActionOptions,
    };

    handleExternalNavigation(navigationConfig);
    this.handleTableMenuOptionAction(data);
  }

  public handleTableMenuOptionAction(event: {
    actionKey: string;
    actionId: string;
    actionOption: ZoneActionOptions;
  }) {
    if (this.context === AccountTypes.SAVINGS_ACCOUNT) {
      this.store.dispatch(new UpdateActiveSavingsAccountId(event.actionId));
    } else {
      this.store.dispatch(new UpdateCurrentAccountId(event.actionId));
    }
    const redirectUrl = this.configService.reRouteIfModal(
      event.actionOption.zoneToNavigate ?? '',
      event.actionOption.urlToNavigate ?? '',
    );
    this.router.navigateByUrl(redirectUrl);
  }

  public searchValue(searchText: string) {
    this.searchText = searchText;
    this.pageNumber = 1;
    this.tablePaginationAndSearch.emit({
      accountContextStatusList: this.accountContextStatusList,
      searchTerm: { name: searchText },
      viewMode: ViewType.TABLE,
      pageSize: this.pageSize,
      page: 1,
    });
  }

  public get pageSize(): number {
    if (this.dataIn.attri['pageSize'] != null) return Number(this.dataIn.attri['pageSize']);
    const provider = this.store.selectSnapshot(ProjectSettingsState.getProjectServiceProvider);
    if (provider === PaymentProvider.SAASCADA) return 5;
    else return 10;
  }
}
